import { ReduceStore } from 'flux/utils';
import Dispatcher from '../dispatcher/appDispatcher';
import { find, get, size } from 'lodash';
import { AUTH_LOGOUT, AUTH_LOGOUT_ACT_AS, GET_ORGANISATION_SETTINGS } from 'constants/actionType';
import accountStore from '../store/accountStore';

/**
 * @enum {string}
 * @mixin
 */
export const organisationSettingsKeys = {
  organisationId: 'organisationId',
  parentShareReviewProcessEnabled: 'parentShareReviewProcessEnabled',
  nextStepObjectivesVisible: 'nextStepObjectivesVisible',
  useObjectiveJudgementForCoverage: 'useObjectiveJudgementForCoverage',
  isObjectiveVisibleToParent: 'isObjectiveVisibleToParent',
  overwriteAllJudgments: 'overwriteAllJudgments',
  organisationNotificationSettings: 'organisationNotificationSettings'
};

/**
 * @enum {string}
 * @mixin
 */
export const organisationNotificationKeys = {
  parentArrival: '2bap:parentArrival',
  parentArrivalMessageText: '2bap:parentArrival:MessageText'
}

/**
 * @typedef {Object} OrganisationSettingsModel
 * @property {number} organisationId
 * @property {boolean} parentShareReviewProcessEnabled
 * @property {boolean} useObjectiveJudgementForCoverage
 * @property {boolean} overwriteAllJudgments
 * @property {boolean} nextStepObjectivesVisible
 * @property {boolean} isObjectiveVisibleToParent
 * @property {Array.<OrganisationNotificationSettings>} organisationNotificationSettings
 */

/**
 * @typedef {Object} OrganisationNotificationSettings
 * @property {organisationNotificationKeys} key
 * @property {string} settingName
 * @property {string} value
 * @property {Array.<OrganisationNotificationSettings>} [subSettings]
 */

export class OrganisationSettingsStore extends ReduceStore {

  static DEFAULT_PARENT_SHARE_REVIEW_PROCESS_ENABLED_VALUE  = false;
  static DEFAULT_NEXT_STEP_OBJECTIVES_VISIBLE = true;
  static DEFAULT_IS_OBJECTIVE_VISIBLE_TO_PARENT = false;

  get parentShareReviewProcessEnabled() { return get(this.currentOrganisationSettings, organisationSettingsKeys.parentShareReviewProcessEnabled); }

  get settingsList() { return get(this.getState(), 'settingsList', []); }

  get currentOrganisationSettings() { return get(this.getState(), 'currentOrganisationSettings', this.getDefaultOrganisationSettings()); }

  get hasSettings() { return size(this.settingsList) > 0; }

  constructor() {
    super(Dispatcher);
  }

  getInitialState() {
    return {
      /** @type {Array<OrganisationSettingsModel>} */
      settingsList: [],
      currentOrganisationSettings: this.getDefaultOrganisationSettings()
    };
  }

  reduce(state, action) {
    switch (action.actionType) {

      case GET_ORGANISATION_SETTINGS:
        return {
          ...state,
          settingsList: action.settingsList,
          currentOrganisationSettings: this.getCurrentOrganisationSettings(action.settingsList)
        };

      case AUTH_LOGOUT:
      case AUTH_LOGOUT_ACT_AS:
        return this.getInitialState();

      default:
        return state;
    }
  }

  getCurrentOrganisationSettings(settingsList) {
    const schoolId  = accountStore.getAccountOrganisationId();
    return this.getSettings(schoolId, settingsList);
  }

  getSettings(schoolId, settingsList = this.settingsList) {
    return find(settingsList, (orgSettings) => { return orgSettings.organisationId === schoolId; });
  }

  getDefaultOrganisationSettings() {
    return {
      organisationId: null,
      parentShareReviewProcessEnabled: OrganisationSettingsStore.DEFAULT_PARENT_SHARE_REVIEW_PROCESS_ENABLED_VALUE,
      nextStepObjectivesVisible: OrganisationSettingsStore.DEFAULT_NEXT_STEP_OBJECTIVES_VISIBLE,
      isObjectiveVisibleToParent: OrganisationSettingsStore.DEFAULT_IS_OBJECTIVE_VISIBLE_TO_PARENT
    }
  }

}

export const organisationSettingsStore = new OrganisationSettingsStore();
